import React, { useEffect, useState } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import DataGrid, { Column, Paging, Scrolling } from 'devextreme-react/cjs/data-grid';
import { OldGoldEntry, OldGoldFormModel } from '../models/old-gold-form-model';
import { GetKaratSelectList, GetMaterialTypeList } from '../../services';
import { GetTodayMayaveGoldRate } from 'modules/transactions/old-gold-request/servises';

interface OldGoldModalProps {
    show: boolean;
    onHide: () => void;
    reloadDataSource?: () => void;
    handelOldGoldnData?: (data: OldGoldEntry[]) => void;
    initialOldGoldnData?: OldGoldEntry[];
}

const OldGoldModal: React.FC<OldGoldModalProps> = ({
    show,
    onHide,
    reloadDataSource,
    handelOldGoldnData,
    initialOldGoldnData
}) => {
    const [loading, setLoading] = useState(false);
    const [oldGoldEntries, setOldGoldEntries] = useState<OldGoldEntry[]>([]);
    const [karatList, setKaratList] = useState<{ value: string; text: string }[]>([]);
    const [todayGoldRate, setTodayGoldRate] = useState<number>(0);
    const [materialTypes, setMaterialTypes] = useState<{ value: string; text: string }[]>([]);

    const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<OldGoldFormModel>({
        defaultValues: {
            material: '',
            kt: '',
            note: ''
        },
        mode: 'onChange'
    });

    const watchedValues = watch();
    useEffect(() => {
        if (show && initialOldGoldnData && initialOldGoldnData.length > 0) {
            setOldGoldEntries([...initialOldGoldnData]);
        } else if (show) {
            setOldGoldEntries([]);
        }
    }, [show, initialOldGoldnData]);

    useEffect(() => {
        if (watchedValues.material && watchedValues.kt && todayGoldRate > 0) {
            setValue('rate', todayGoldRate);
        }
    }, [watchedValues.material, watchedValues.kt, todayGoldRate, setValue]);

    useEffect(() => {
        const rate = watchedValues.rate || 0;
        const purity = watchedValues.purity || 0;
        const netWt = watchedValues.netWt || 0;

        if (rate > 0 && purity > 0 && netWt > 0) {
            const equivalentWt = (netWt * purity) / 100;
            const totalAmt = equivalentWt * rate;

            setValue('equivalentWt', Number(equivalentWt?.toFixed(3)));
            setValue('totalAmt', Number(totalAmt?.toFixed(2)));
        } else {
            setValue('equivalentWt', 0);
            setValue('totalAmt', 0);
        }
    }, [watchedValues.rate, watchedValues.purity, watchedValues.netWt, setValue]);

    const onAddEntry = (data: OldGoldFormModel) => {
        if (!data.material || !data.kt || data.rate <= 0) {
            toast.error('Please fill all required fields');
            return;
        }

        const newEntry: OldGoldEntry = {
            id: Date.now().toString(),
            material: data.material,
            kt: data.kt,
            rate: data.rate,
            purity: data.purity,
            netWt: data.netWt,
            equivalentWt: data.equivalentWt,
            totalAmt: data.totalAmt,
            note: data.note
        };

        const updatedEntries = [...oldGoldEntries, newEntry];
        setOldGoldEntries(updatedEntries);

        if (handelOldGoldnData) {
            handelOldGoldnData(updatedEntries);
        }
        handleClose();
        toast.success('Entry added successfully!');
    };

    const handleSave = async () => {
        if (oldGoldEntries.length === 0) {
            toast.error('Please add at least one entry');
            return;
        }

        try {
            setLoading(true);
            await new Promise(resolve => setTimeout(resolve, 1000))
            if (handelOldGoldnData) {
                handelOldGoldnData(oldGoldEntries);
            }

            toast.success('Old Gold Purchase saved successfully!');
            if (reloadDataSource) {
                reloadDataSource();
            }
            handleClose();
        } catch (error: any) {
            toast.error(error.message || 'Error saving old gold purchase');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOldGoldEntries([]);
        reset();
        onHide();
    };

    const removeEntry = (id: string) => {
        const updatedEntries = oldGoldEntries.filter(entry => entry.id !== id);
        setOldGoldEntries(updatedEntries);

        if (handelOldGoldnData) {
            handelOldGoldnData(updatedEntries);
        }

        toast.info('Entry removed');
    };

    const renderHeaderCell = (headerInfo: any) => {
        return <div className="text-nowrap">{headerInfo.column.caption}</div>;
    };

    const renderActionCell = (cellData: any) => {
        return (
            <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeEntry(cellData.data.id)}
                title="Remove Entry"
            >
                <i className="bx bx-trash"></i>
            </button>
        );
    };

    const renderCurrencyCell = (cellData: any) => {
        return `₹${cellData.value?.toFixed(2)}`;
    };

    const renderWeightCell = (cellData: any) => {
        return `${cellData.value?.toFixed(2)}`;
    };

    useEffect(() => {
        if (show) {
            MaterialTypeListData();
        }
    }, [show]);

    const MaterialTypeListData = async () => {
        try {
            setLoading(true);

            const [material, karats] = await Promise.all([
                GetMaterialTypeList(),
                GetKaratSelectList(),
            ]);

            const goldMaterial = material?.find((item: any) => item?.text?.toLowerCase() === "gold");
            const karat24KT = karats?.find((item: any) => item?.text?.toLowerCase() === "24kt");

            setMaterialTypes(material || []);
            setKaratList(karats || []);

            if (goldMaterial) {
                setValue('material', goldMaterial.text);
            }

            if (karat24KT) {
                setValue('kt', karat24KT.text);

                try {
                    const todayGoldRateData = await GetTodayMayaveGoldRate(karat24KT.value);
                    setTodayGoldRate(todayGoldRateData.data.metalRate || 0);
                    setValue('rate', todayGoldRateData.data.metalRate || 0);
                } catch (rateError) {
                    console.log('Error fetching gold rate:', rateError);
                    setTodayGoldRate(0);
                    setValue('rate', 0);
                }
            }
        } catch (error: any) {
            console.log(error.message);
            setMaterialTypes([]);
            setKaratList([]);
            setTodayGoldRate(0);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                show={show}
                size='lg'
                onHide={handleClose}
                className="fade"
                dialogClassName="modal-dialog-centered"
                backdrop="static"
            >
                <Modal.Header closeButton className="border-0">
                    <div className="d-flex align-items-center">
                        <i className="bx bx-coins text-warning me-2"></i>
                        <span className="fw-bold ">Old Gold Purchase Entry</span>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col lg={12}>
                            <form onSubmit={handleSubmit(onAddEntry)}>
                                <div className="row">
                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Material <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-control form-control-sm form-select form-select-sm ${errors.material ? "is-invalid" : ""}`}
                                            {...register("material", { required: "Material is required" })}
                                            // disabled={true}
                                            value={watchedValues.material || ''}
                                        >
                                            {materialTypes?.length > 0 &&
                                                materialTypes
                                                    .filter((m) => m.text?.toLowerCase() === "gold")
                                                    .map((m) => (
                                                        <option key={m.value} value={m.text}>
                                                            {m.text}
                                                        </option>
                                                    ))}
                                        </select>
                                        {errors.material && <span className="text-danger small">{String(errors.material.message)}</span>}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Karat  <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-control form-control-sm form-select form-select-sm ${errors.kt ? "is-invalid" : ""}`}
                                            {...register("kt", { required: "Kt is required" })}
                                            disabled={true}
                                            value={watchedValues.kt || ''}
                                        >
                                            <option value="">Select</option>
                                            {karatList?.length > 0 &&
                                                karatList.map((k) => (
                                                    <option key={k.value} value={k.text}>
                                                        {k.text}
                                                    </option>
                                                ))}
                                        </select>
                                        {errors.kt && <span className="text-danger small">{String(errors.kt.message)}</span>}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Rate per gram <span className="text-danger">*</span></label>
                                        <input
                                            className={`form-control form-control-sm ${errors.rate ? "is-invalid" : ""}`}
                                            type="number"
                                            step="0.01"
                                            disabled
                                            placeholder="0"
                                            {...register("rate", {
                                                required: "Rate is required",
                                                min: { value: 0.01, message: "Rate must be greater than 0" }
                                            })}
                                        />
                                        {errors.rate && <span className="text-danger small">{String(errors.rate.message)}</span>}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Purity (%) <span className="text-danger">*</span></label>
                                        <input
                                            className={`form-control form-control-sm ${errors.purity ? "is-invalid" : ""}`}
                                            type="number"
                                            step="0.01"
                                            placeholder="0"
                                            max="100"
                                            {...register("purity", {
                                                required: "Purity is required",
                                                min: { value: 1, message: "Purity is required" },
                                                max: { value: 100, message: "Purity cannot exceed 100%" },
                                                onChange: (e) => {
                                                    const val = e.target.value;
                                                    const numVal = parseFloat(val);
                                                    if (
                                                        val === "" ||
                                                        (/^\d{0,3}(\.\d{0,2})?$/.test(val) && (!isNaN(numVal) && numVal <= 100))
                                                    ) {
                                                        e.target.value = val;
                                                    } else {
                                                        e.target.value = val.slice(0, -1);
                                                    }
                                                }
                                            })}
                                        />
                                        {errors.purity && <span className="text-danger small">{String(errors.purity.message)}</span>}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Net Wt (gm) <span className="text-danger">*</span></label>
                                        <input
                                            className={`form-control form-control-sm ${errors.netWt ? "is-invalid" : ""}`}
                                            type="number"
                                            step="0.001"
                                            placeholder="0"
                                            max={10000}
                                            {...register("netWt", {
                                                required: "Net weight is required",
                                                min: { value: 0.001, message: "Net weight must be greater than 0" },
                                                max: { value: 10000, message: "Net weight cannot exceed 10,000" }
                                            })}
                                        />
                                        {errors.netWt && <span className="text-danger small">{String(errors.netWt.message)}</span>}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Equivalent Wt (gm)</label>
                                        <input
                                            className="form-control form-control-sm"
                                            type="number"
                                            step="0.001"
                                            placeholder="0"
                                            {...register("equivalentWt")}
                                            readOnly
                                            disabled
                                        />
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Total Amt (₹)</label>
                                        <input
                                            className="form-control form-control-sm"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            {...register("totalAmt")}
                                            readOnly
                                            disabled
                                        />
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label text-muted small">Note</label>
                                        <input
                                            className={`form-control form-control-sm ${errors.note ? "is-invalid" : ""}`}
                                            placeholder="Note"
                                            maxLength={150}
                                            {...register("note", {
                                                maxLength: {
                                                    value: 150,
                                                    message: "Note must be 150 characters or less",
                                                },
                                            })}
                                            onInput={(e: any) => {
                                                let value = e.target.value.replace(/\s+/g, ' ');
                                                if (value.startsWith(' ')) {
                                                    value = value.trimStart();
                                                }
                                                e.target.value = value;
                                            }}
                                        />
                                        {errors.note && (
                                            <div className="invalid-feedback">{errors.note.message}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="d-flex justify-content-end mb-4">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-sm px-4"
                                        disabled={loading}
                                    >
                                        <i className="bx bx-plus me-1"></i>
                                        Add Entry
                                    </button>
                                </div>
                            </form>

                            {oldGoldEntries.length > 0 && (
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold mb-0">Old Gold Entries ({oldGoldEntries.length})</h6>
                                        <div className="text-muted small">
                                            Total Amount: ₹{oldGoldEntries.reduce((sum, entry) => sum + entry.totalAmt, 0)?.toFixed(2)}
                                        </div>
                                    </div>
                                    <DataGrid
                                        dataSource={oldGoldEntries}
                                        keyExpr="id"
                                        showBorders={true}
                                        showRowLines={true}
                                        allowColumnReordering={true}
                                        allowColumnResizing={true}
                                        columnAutoWidth={true}
                                        wordWrapEnabled={true}
                                    >
                                        <Paging defaultPageSize={10} />
                                        <Scrolling mode="standard" />
                                        <Column
                                            caption="Action"
                                            width={80}
                                            cellRender={renderActionCell}
                                            headerCellRender={renderHeaderCell}
                                            allowSorting={false}
                                            allowFiltering={false}
                                        />
                                        <Column
                                            dataField="material"
                                            caption="Material"
                                            dataType="string"
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="kt"
                                            caption="Kt"
                                            dataType="string"
                                            width={80}
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="rate"
                                            caption="Rate"
                                            dataType="number"
                                            format="#,##0.00"
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="purity"
                                            caption="Purity (%)"
                                            dataType="number"
                                            format="#,##0.00"
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="netWt"
                                            caption="Net Wt"
                                            dataType="number"
                                            format="#,##0.00"
                                            cellRender={renderWeightCell}
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="equivalentWt"
                                            caption="Equiv Wt"
                                            dataType="number"
                                            format="#,##0.00"
                                            cellRender={renderWeightCell}
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="totalAmt"
                                            caption="Total Amt"
                                            dataType="number"
                                            format="#,##0.00"
                                            cellRender={renderCurrencyCell}
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="note"
                                            caption="Note"
                                            dataType="string"
                                            headerCellRender={renderHeaderCell}
                                            width={150}
                                            cellRender={({ data }) => (
                                                <div
                                                    title={data.note}
                                                    style={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                    {data.note}
                                                </div>
                                            )}
                                        />
                                        <Scrolling
                                            mode='standard'
                                            useNative={true}
                                            scrollByContent={true}
                                            scrollByThumb={true}
                                        />
                                    </DataGrid>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn add-btn btn-sm px-4"
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn submit-btn  px-4"
                            onClick={handleSave}
                            disabled={loading || oldGoldEntries.length === 0}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default OldGoldModal;